import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
   subscribeSubPopup,
   getCurrentSubPopup,
   closeSubPopup as requestCloseSubPopup,
   popSubPopup,
} from "./popupUIStore";
import { triggerHapticFeedback } from "../../utils/hapticFeedback";
import { ReactSVG } from "react-svg";
import addIcon from "../../assets/svg/add-s.svg";
import ReservationEditPopup from "../Popups/EditReservation/EditReservation";

function resolveSubPopupPortalHost() {
   if (typeof document === "undefined") return null;
   const doc = document;
   const fullscreenEl = doc.fullscreenElement || doc.webkitFullscreenElement;
   if (
      !fullscreenEl ||
      fullscreenEl === doc.documentElement ||
      fullscreenEl === doc.body
   ) {
      return doc.body;
   }
   return fullscreenEl;
}

export default function SubPopupUI() {
   const [portalHost, setPortalHost] = useState(() =>
      resolveSubPopupPortalHost(),
   );
   const [popupState, setPopupState] = useState({
      type: getCurrentSubPopup()?.type || null,
      props: getCurrentSubPopup()?.props || {},
   });
   const [exiting, setExiting] = useState(false);
   const [armed, setArmed] = useState(false);
   const panelRef = useRef(null);

   // cheie care forteaza remount la fiecare OPEN de subpopup
   const [openKey, setOpenKey] = useState(0);

   const isMobile = () =>
      typeof window !== "undefined" &&
      window.matchMedia("(max-width: 860px)").matches;

   // starea SubPopup in history
   const subSessionIdRef = useRef(0);
   const subDepthRef = useRef(0);

   // back initiat de gest (popstate) ca sa nu mai dam inca un history.back() dupa animatie
   const closingByPopstateRef = useRef(false);

   // cand inchidem top-ul si reapare precedentul (nu mai push-uim history)
   const revealingPrevRef = useRef(false);

   const handleClose = () => {
      triggerHapticFeedback();
      requestCloseSubPopup();
   };

   useEffect(() => {
      if (typeof document === "undefined") return undefined;

      const syncPortalHost = () => {
         setPortalHost(resolveSubPopupPortalHost());
      };

      syncPortalHost();
      document.addEventListener("fullscreenchange", syncPortalHost);
      document.addEventListener("webkitfullscreenchange", syncPortalHost);

      return () => {
         document.removeEventListener("fullscreenchange", syncPortalHost);
         document.removeEventListener("webkitfullscreenchange", syncPortalHost);
      };
   }, []);

   // Back/gest — daca exista un SubPopup deschis, il inchidem noi (nu Popup)
   useEffect(() => {
      const onPopState = () => {
         if (getCurrentSubPopup()) {
            closingByPopstateRef.current = true;
            requestCloseSubPopup();
            return;
         }
      };
      window.addEventListener("popstate", onPopState);
      return () => window.removeEventListener("popstate", onPopState);
   }, []);

   useEffect(() => {
      const handleRequestClose = () => {
         if (!getCurrentSubPopup()) {
            closingByPopstateRef.current = false;
            return;
         }

         // CLOSE REQUEST
         setExiting(false);
         setArmed(false);
         document.body.classList.remove("subpopup-open");

         // scoatem top-ul din stiva imediat
         revealingPrevRef.current = true;
         popSubPopup();

         // pe mobil: consumam o intrare din istorie DOAR daca inchiderea nu a venit din popstate
         if (isMobile() && subDepthRef.current > 0) {
            if (!closingByPopstateRef.current) {
               window.history.back();
            }
            subDepthRef.current -= 1;
         }
         closingByPopstateRef.current = false;
      };

      const unsub = subscribeSubPopup(({ detail, action }) => {
         if (action === "request-close") {
            handleRequestClose();
            return;
         }

         if (detail) {
            // OPEN / REVEAL
            setExiting(false);
            setArmed(false);
            document.body.classList.add("subpopup-open");
            setPopupState({
               type: detail.type,
               props: { ...(detail.props || {}) },
            });

            // forteaza remount doar cand deschidem ceva nou (nu cand reapare precedentul)
            if (!revealingPrevRef.current) {
               subSessionIdRef.current += 1;
               setOpenKey(subSessionIdRef.current);
            }

            requestAnimationFrame(() =>
               requestAnimationFrame(() => setArmed(true)),
            );

            // fiecare SubPopup deschis impinge o intrare separata in istorie (stack)
            if (isMobile() && !revealingPrevRef.current) {
               const sid = subSessionIdRef.current;
               window.history.pushState(
                  { __subpopup_ui_dummy: true, sid },
                  "",
                  window.location.href,
               );
               subDepthRef.current += 1;
            }
            revealingPrevRef.current = false;
            return;
         }

         // STACK EMPTY
         setExiting(false);
         setArmed(false);
         document.body.classList.remove("subpopup-open");
         setPopupState({ type: null, props: {} });
         revealingPrevRef.current = false;
      });

      return () => {
         document.body.classList.remove("subpopup-open");
         unsub();
      };
   }, []);

   const renderContent = () => {
      switch (popupState.type) {
         case "reservationEdit":
            return (
               <ReservationEditPopup
                  key={openKey}
                  onClose={handleClose}
                  {...popupState.props}
               />
            );
         default: {
            const Cmp = popupState.props?.component;
            return Cmp ? (
               <Cmp key={openKey} onClose={handleClose} {...popupState.props} />
            ) : null;
         }
      }
   };

   if (!popupState.type) return null;

   const tree = (
      <>
         <div
            className={`subpopup-panel__overlay ${armed ? "is-armed" : ""}`}
            onClick={handleClose}
         />
         <div
            className={`subpopup-panel ${exiting ? "subpopup-exit" : ""}`}
            ref={panelRef}
            onClick={(e) => e.stopPropagation()}
         >
            <ReactSVG
               onClick={handleClose}
               className="subpopup-panel__close react-icon rotate45"
               src={addIcon}
            />
            <div className="subpopup-panel__inner">{renderContent()}</div>
         </div>
      </>
   );

   return portalHost ? createPortal(tree, portalHost) : tree;
}
